import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img from "@insite/mobius/Img";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ELEMENTS, TYPOGRAPHY, WIDGETS } from "@tSurya/theme/design";
import StyledLink from "@tSurya/theme/components/StyledLink";
import React from "react";
import { css } from "styled-components";

const W = (WIDGETS as any).brandPillars || {};

const enum fields {
    backgroundColor = "backgroundColor",
    paddingTopBottom = "paddingTopBottom",
    paddingLeftRight = "paddingLeftRight",
    mobilePaddingTopBottom = "mobilePaddingTopBottom",
    mobilePaddingLeftRight = "mobilePaddingLeftRight",
    items = "items",
}

interface ItemModel {
    fields: {
        icon: string;
        iconAlt: string;
        title: string;
        description: string;
        ctaLabel: string;
        ctaLink: LinkFieldValue;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.backgroundColor]: string;
        [fields.paddingTopBottom]: string;
        [fields.paddingLeftRight]: string;
        [fields.mobilePaddingTopBottom]: string;
        [fields.mobilePaddingLeftRight]: string;
        [fields.items]: ItemModel[];
    };
}

type Props = OwnProps;

export interface SurBrandPillarsStyles {
    container?: InjectableCss;
    innerWrapper?: InjectableCss;
    pillar?: InjectableCss;
    iconCircle?: InjectableCss;
    iconImage?: InjectableCss;
    textBlock?: InjectableCss;
    title?: TypographyProps;
    description?: InjectableCss;
    ctaWrapper?: InjectableCss;
}

export const surBrandPillarsStyles: SurBrandPillarsStyles = {
    container: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
        `,
    },
    innerWrapper: {
        css: css`
            display: flex;
            gap: ${W.columnsGap || "60px"};
            max-width: ${W.maxWidth || "1360px"};
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        gap: ${W.mobileColumnsGap || "40px"};
                    `,
                    css`
                        flex-direction: column;
                        gap: ${W.mobileColumnsGap || "40px"};
                    `,
                    css`
                        flex-direction: column;
                        gap: ${W.mobileColumnsGap || "40px"};
                    `,
                    null,
                    null,
                ])}
        `,
    },
    pillar: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: ${W.pillarGap || "20px"};
            flex: 1;
            min-width: 0;
        `,
    },
    iconCircle: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            background: ${W.iconCircle?.background || "#EFEAE0"};
            border-radius: 50%;
            flex-shrink: 0;

            img {
                width: ${W.iconCircle?.iconSize || "64px"};
                height: ${W.iconCircle?.iconSize || "64px"};
                object-fit: contain;
            }
        `,
    },
    iconImage: {
        css: css`
            width: ${W.iconCircle?.iconSize || "64px"};
            height: ${W.iconCircle?.iconSize || "64px"};
        `,
    },
    textBlock: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${W.textGap || "8px"};
            text-align: center;
            width: 100%;
        `,
    },
    title: {
        variant: "h4",
        css: css`
            font-family: ${W.title?.fontFamily || TYPOGRAPHY.fontFamily.title};
            font-size: ${W.title?.fontSize || "28px"};
            font-weight: ${W.title?.fontWeight || "500"};
            line-height: ${W.title?.lineHeight || "normal"};
            color: ${getColor("text.main")};
            text-align: center;
            margin: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`font-size: ${W.title?.mobileFontSize || "24px"};`,
                    css`font-size: ${W.title?.mobileFontSize || "24px"};`,
                    css`font-size: ${W.title?.mobileFontSize || "24px"};`,
                    null,
                    null,
                ])}
        `,
    },
    description: {
        css: css`
            font-family: ${W.description?.fontFamily || TYPOGRAPHY.fontFamily.base};
            font-size: ${W.description?.fontSize || "14px"};
            font-weight: ${W.description?.fontWeight || "400"};
            line-height: ${W.description?.lineHeight || "1.5"};
            letter-spacing: ${W.description?.letterSpacing || "0.08px"};
            color: ${getColor("text.main")};
            text-align: center;
            margin: 0;

            p { margin: 0; }
        `,
    },
    ctaWrapper: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
};

const styles = surBrandPillarsStyles;

const SurBrandPillars: React.FC<Props> = ({ fields: f }) => {
    const itemLinks = useGetLinks(f.items, o => o.fields.ctaLink);
    const bgColor = f.backgroundColor || "transparent";

    const paddingTB = f.paddingTopBottom || "100px";
    const paddingLR = f.paddingLeftRight || "40px";
    const mobilePaddingTB = f.mobilePaddingTopBottom || "32px";
    const mobilePaddingLR = f.mobilePaddingLeftRight || "16px";

    const containerCss: InjectableCss = {
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
            padding: ${paddingTB} ${paddingLR};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`padding: ${mobilePaddingTB} ${mobilePaddingLR};`,
                    css`padding: ${mobilePaddingTB} ${mobilePaddingLR};`,
                    css`padding: ${mobilePaddingTB} ${mobilePaddingLR};`,
                    null,
                    null,
                ])}
        `,
    };

    return (
        <StyledWrapper {...containerCss} style={{ backgroundColor: bgColor }}>
            <StyledWrapper {...styles.innerWrapper}>
                {f.items.map((item: ItemModel, index: number) => {
                    const ctaUrl = itemLinks.links[index]?.url || "";
                    const hasCta = !!item.fields.ctaLabel && !!ctaUrl;
                    const stableKey =
                        item.fields.icon ||
                        item.fields.title ||
                        String(index);

                    return (
                        <StyledWrapper key={stableKey} {...styles.pillar}>
                            {item.fields.icon && (
                                <StyledWrapper {...styles.iconCircle}>
                                    <Img
                                        src={item.fields.icon}
                                        altText={item.fields.iconAlt || item.fields.title || ""}
                                    />
                                </StyledWrapper>
                            )}
                            <StyledWrapper {...styles.textBlock}>
                                <Typography {...styles.title} as="h4" role="heading">
                                    {item.fields.title}
                                </Typography>
                                {item.fields.description && (
                                    <StyledWrapper
                                        {...styles.description}
                                        dangerouslySetInnerHTML={{ __html: item.fields.description }}
                                    />
                                )}
                            </StyledWrapper>
                            {hasCta && (
                                <StyledWrapper {...styles.ctaWrapper}>
                                    <StyledLink href={ctaUrl} textColor={(ELEMENTS as any).ctaLink?.color}>{item.fields.ctaLabel}</StyledLink>
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: SurBrandPillars,
    definition: {
        group: "Basic",
        displayName: "Sur Brand Pillars",
        fieldDefinitions: [
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "TextField",
                defaultValue: "rgba(242,242,242,0.6)",
                fieldType: "General",
                tooltip: "CSS color value for the container background.",
                placeholder: "rgba(242,242,242,0.6)",
                sortOrder: 0,
            },
            {
                name: fields.paddingTopBottom,
                displayName: "Padding Top/Bottom (Desktop)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "CSS value for top and bottom padding on desktop. Default: 100px",
                placeholder: "100px",
                sortOrder: 1,
            },
            {
                name: fields.paddingLeftRight,
                displayName: "Padding Left/Right (Desktop)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "CSS value for left and right padding on desktop. Default: 40px",
                placeholder: "40px",
                sortOrder: 2,
            },
            {
                name: fields.mobilePaddingTopBottom,
                displayName: "Padding Top/Bottom (Mobile)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "CSS value for top and bottom padding on mobile. Default: 32px",
                placeholder: "32px",
                sortOrder: 3,
            },
            {
                name: fields.mobilePaddingLeftRight,
                displayName: "Padding Left/Right (Mobile)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "CSS value for left and right padding on mobile. Default: 16px",
                placeholder: "16px",
                sortOrder: 4,
            },
            {
                name: fields.items,
                displayName: "Pillars",
                editorTemplate: "ListField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 10,
                getDisplay: (item: HasFields) => {
                    return item.fields.title || "(no title)";
                },
                fieldDefinitions: [
                    {
                        name: "icon",
                        displayName: "Icon (PNG)",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "iconAlt",
                        displayName: "Icon Alt Text",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "title",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "description",
                        displayName: "Description",
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                    },
                    {
                        name: "ctaLabel",
                        displayName: "CTA Label",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "ctaLink",
                        displayName: "CTA Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
